import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Download } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { mediaUrl } from '../lib/api';
import api from '../lib/api';
import { useCart } from '../context/CartContext';

const OrderSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | error

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/shop/orders/${sessionId}`);
        setOrder(res.data);
        if (res.data.payment_status === 'paid') { setStatus('ok'); clear(); return; }
      } catch { /* ignore */ }
      tries += 1;
      if (tries < 5) setTimeout(poll, 1500);
      else setStatus('ok');
    };
    poll();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="App" data-testid="page-order-success">
      <Header />
      <main className="bg-cream min-h-[60vh]">
        <section className="max-w-[820px] mx-auto px-6 lg:px-10 py-20 text-center">
          {status === 'loading' ? (
            <div className="flex flex-col items-center gap-3 text-mahogany"><Loader2 className="w-8 h-8 animate-spin" /> Confirming your payment…</div>
          ) : status === 'error' ? (
            <div><p className="text-mahogany/70">We couldn’t find your order.</p><Link to="/shop" className="pill-btn mt-4 inline-flex">Back to Shop</Link></div>
          ) : (
            <>
              <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
              <h1 className="font-serif-display text-4xl md:text-5xl text-burgundy">Thank <span className="font-italic-display text-rose-italic">You</span></h1>
              <p className="mt-4 text-mahogany/80">
                {order?.payment_status === 'paid'
                  ? 'Your order is confirmed. A receipt has been sent to your email.'
                  : 'Your order has been received. If payment is still processing, your downloads will appear here shortly.'}
              </p>

              {order?.items?.length > 0 && (
                <div className="mt-10 text-left bg-white/70 rounded-3xl border border-rose/40 p-6" data-testid="order-items">
                  <div className="flex items-center justify-between mb-4">
                    <div className="font-serif-display text-2xl text-burgundy">Your Order</div>
                    {order.order_number && <span className="text-sm text-mahogany/70">{order.order_number}</span>}
                  </div>
                  <ul className="divide-y divide-rose/30">
                    {order.items.map((it, i) => (
                      <li key={`${it.product_id || it.title}-${i}`} className="py-3 flex items-center justify-between gap-4">
                        <div>
                          <span className="text-burgundy font-medium">{it.title}</span>
                          <span className="text-mahogany/60 text-sm"> × {it.quantity}</span>
                          {it.product_type === 'digital' && <span className="ml-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-mahogany text-cream">Digital</span>}
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-mahogany">${(it.price * it.quantity).toFixed(2)}</span>
                          {it.digital_file_url && (
                            <a href={mediaUrl(it.digital_file_url)} target="_blank" rel="noreferrer" download
                              className="pill-btn py-1.5 text-sm" data-testid={`download-${i}`}>Download <Download className="w-4 h-4" /></a>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between pt-4 mt-2 border-t border-rose/40 text-burgundy font-medium">
                    <span>Total</span><span>${(order.total || 0).toFixed(2)}</span>
                  </div>
                  {order.shipping_address?.line1 && (
                    <div className="mt-4 pt-3 border-t border-rose/30 text-sm text-mahogany/75">
                      <span className="font-medium text-burgundy">Shipping to:</span> {[order.shipping_address.name, order.shipping_address.line1, order.shipping_address.city, order.shipping_address.state, order.shipping_address.postal_code, order.shipping_address.country].filter(Boolean).join(', ')}
                    </div>
                  )}
                </div>
              )}
              <div className="mt-8 flex justify-center gap-3">
                <Link to="/shop" className="pill-btn-outline">Continue Shopping</Link>
                <Link to="/dashboard/payments" className="pill-btn">My Payments</Link>
              </div>
            </>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default OrderSuccess;
